import { ITCITaggable } from "./tci-app-tags";
import { TCITenantAppType } from "./tci-application";
import { ITCITenantComponentInput, ITCITenantComponentOutput } from "./tci-component";
import {
    AbstractTCITenantComponentOptions,
    ITCIOptionType,
    ITCITenantComponentOptions,
    ITCITenantModalOptions,
    ITCITenantModalType,
    ITCITenantServiceProvider,
    TCITenantModalType,
    TCITenantSelectorType
} from "./tci-tenant";

import { Observable, Subject } from "rxjs";
import { strEnum } from "../../types";
import { ITCIUserInfo } from "./tci-user";

/**
 *  TCI Connection Detail I/O
 *
 */

/**
 * interface ITCIConnectorDetailInput
 */
export interface ITCIConnectorDetailInput extends ITCITenantComponentInput {
    connectorId: string;
    connector: ITCIConnector;
}

/**
 * interface ITCIConnectorDetailOutput
 */
export interface ITCIConnectorDetailOutput<T extends ITCIConnectorEventData> extends ITCITenantComponentOutput<T> {
}

/**
 * interface ITCIConnectorEventData
 */
export interface ITCIConnectorEventData {
    /**
     * connector selected in marketplace being manipulated
     */
    connector: ITCIConnector;

    /**
     * isWorking indicates if an API request is awaiting response
     * Useful to indicate if a connector is installing or uninstalling
     */
    isWorking: boolean;
}

/**
 * interface ITCIConnection
 */
export interface ITCIConnection extends ITCITaggable {
    /**
     * connection id
     */
    id: string;
    /**
     * description
     */
    description: string;
    /**
     * name
     */
    name: string;
    /**
     * tenant type for the connection
     */
    tenantType: TCITenantAppType;
    /**
     * connector instance for the connection
     */
    connector?: ITCIConnector;
    /**
     * connector id
     */
    connectorId: string;
    /**
     * Owner organization Id
     */
    orgId?: string;
    /**
     * Owner/User Id
     */
    userId?: string;
    /**
     * Date created
     */
    created_dt?: number;
    /**
     * Date last modified
     */
    lastModified_dt?: number;
    /**
     * lazy loaded usage in apps with app id/solution id reference
     */
    appUsage?: string[];
    /**
     * Error information from tenant server
     */
    error?: { message: string, statusMessage: string };
    /**
     * Flag to identify global connection
     */
    isGlobal?: boolean;

    /**
     * Visibility information ("ORG" for Scribe as default)
     */
    visibility?: TCIConnectionVisibility;
}

/**
 * interface ITCIConnector
 */
export interface ITCIConnector {
    /**
     * Connector id - unique identifier for the connector type
     */
    id: string;
    /**
     * connector name or label
     */
    name: string;
    /**
     * Connector description
     */
    description: string;
    /**
     * Connector Tech Type
     */
    tenantType: TCITenantAppType;
    /**
     * Specifies the small icon
     */
    smallIcon: string;
    /**
     * Specifies the small icon
     */
    largeIcon: string;
    /**
     * TCIConnectorTechType icon i.e. scribe/flogo
     */
    techIcon: string;
    /**
     *  Distributor entity logo i.e. TIBCO / Third party
     */
    distIcon: string;
    /**
     * Connector details
     */
    details: ITCIConnectorDetails;
}

export interface ITCIConnectorDetails {
    /**
     * version of the connector
     */
    version: string;
    /**
     * Connector published
     */
    publisher?: string;
    /**
     * publishing date
     */
    published_dt?: Date;
    /**
     * calculated market place url for the connector
     */
    marketPlaceUrl?: string;
}

export interface ITCIConnectorIconReqResp {
    artifactId: string;
    smallIcon: string;
    largeIcon: string;
}

export interface ITCIGetConnectorsRequestError {
    type: TCITenantAppType;
    status: number;
}

export interface ITCIGetConnectorRequest {
    id: string;
    tenantType: TCITenantAppType;
}

export interface ITCIGetConnectorsResponse {
    connectors: ITCIConnector[];
    errors: ITCIGetConnectorsRequestError[];
}

/**
 * TCI_CONNECTION_ACTION_TYPE Type enumerations
 * @type {{[K in string | string | string | string | string | string | string | string]:K}}
 */
export const TCI_CONNECTION_ACTION_TYPE = strEnum([
    "CONN_DELETE",
    "CONN_ADD",
    "CONN_EDIT",
    "CONN_REFRESH",
    "CONN_START_SUBSCRIPTION",
    "CONN_STOP_SUBSCRIPTION",
    "CONN_START_SUBSCRIPTION",
    "CONN_APP_COUNT",
    "CONN_CHANGE_VISIBILITY",
    "CONN_DELETE_TOOLTIP",
    "CONN_STATUS",
    "CONN_APP_NAME"
]);
export type TCI_CONNECTION_ACTION_TYPE = keyof typeof TCI_CONNECTION_ACTION_TYPE;

/**
 * Connection Service Notification Types
 */
export const enum TCIConnectionServiceNotificationType {
    /**
     * Refresh notification with connected app count
     */
    NOTIFICATION_REFRESH = 0,
    /**
     * Update notification to indicate a connection is being subscribed by an app
     */
    NOTIFICATION_CONNECTION_APP_SUBSCRIBE = 1,
    /**
     * Update notification to indicate a connection is being unsubscribed by an app
     */
    NOTIFICATION_CONNECTION_APP_UNSUBSCRIBE = 2,
    /**
     * Notifying the addition of a new uploaded connector from extensions
     */
    NOTIFICATION_CONNECTOR_ADDED = 3,
    /**
     * Notifying the removal of an uploaded connector from extensions
     */
    NOTIFICATION_CONNECTOR_REMOVED = 4,
    /**
     * Notify when a connection is added
     */
    NOTIFICATION_CONNECTION_ADDED = 5,
    /**
     * Notify when a connection is dropped
     */
    NOTIFICATION_CONNECTION_REMOVED = 6
}

export interface ITCIConnectionStatus {
    /**
     * Get status Tooltip text
     */
    getToolTip(): string;

    /**
     * Get status icon url
     */
    getIconUrl(): string;

    /**
     * Get status Tooltip title
     */
    getTitle(): string;
}

export class TCIDefaultConnectionStatus implements ITCIConnectionStatus {
    constructor(private tooltip = "", private url = "", private title = "Connection Error") {
    }

    getToolTip(): string {
        return this.tooltip;
    }

    getIconUrl(): string {
        return this.url;
    }

    getTitle(): string {
        return this.title;
    }
}

export interface ITCIConnectionRequest {
    actionType: TCI_CONNECTION_ACTION_TYPE | string;
    connection?: ITCIConnection;
    connector?: ITCIConnector;
    userInfo?: ITCIUserInfo;
}

export interface ITCIConnectionResponse {
    /**
     * connector Id
     */
    connectorId?: string;
    /**
     * connection Id
     */
    connectionId?: string;
    /**
     * tenant type
     */
    tenantType?: TCITenantAppType | string;
    /**
     * Used in UI request/response
     */
    actionType?: TCI_CONNECTION_ACTION_TYPE | string;
    /**
     * Notification to the caller based on live updates on the connection subject
     */
    notificationType?: TCIConnectionServiceNotificationType;
    /**
     * Connection visibility Type
     */
    visibilityType?: TCIConnectionVisibility;
    /**
     * Used in UI request/response
     */
    connection?: ITCIConnection;
    /**
     * connection status
     */
    connectionStatus?: ITCIConnectionStatus;
    /**
     * Number of applications using this connection given by the connectionId
     */
    numConnectedApps?: number;
    /**
     * application id's using this connection given by the connectionId
     */
    connectedApps?: string[];
    /**
     * errors
     */
    error?: string;
}

/**
 * Provider service to the connections UI to get connected apps and connection life cycle hook
 * to be implemented by tenants
 */
export interface ITCIConnectionServiceProvider extends ITCITenantServiceProvider {
    /**
     * returns list of connectors
     * This is a override mechanism to let flogo tenant
     * provide connectors via UI service code from studio cache.
     * For scribe the base class should call WS /connectors API
     */
    getConnectors(): Observable<ITCIConnector[]>;

    /**
     * Returns a list of connections from individual provider
     * @return @Observable<ITCIConnection[]>
     */
    getConnections(): Observable<ITCIConnection[]>;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param request: @ITCIConnectionRequest
     * @return @Observable<boolean>
     */
    startConnectionSubscription(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param request: @ITCIConnectionRequest
     * @return @Observable<boolean>
     */
    stopConnectionSubscription(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Returns connections Subject, the UI consumer will subscribe and unsubscribe from this
     * subject when the UI is active and inactive respectively. The provider's responsibility is to
     * control how frequently it needs to emit on the subject. There is only one subject per provider
     * which will emit per connection status change with ITCIConnection in the @ITCIConnectionServiceResponse. The
     * subscriber will update the respective app status based on the response
     * @return Subject<ITCIConnectionResponse>
     */
    getConnectionsSubject(): Subject<ITCIConnectionResponse>;

    /**
     * check before creating a connection using the editor
     * @param request
     */
    canCreate(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * create the connection by using the router which can be injected on the
     * tenant implementation
     * @param request
     * @param returnUrl
     */
    createConnection(request: ITCIConnectionRequest, returnUrl: string): Observable<ITCIConnectionResponse>;

    /**
     * check before opening a connection editor for editing
     * @param request
     */
    canEdit(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * edit the connection by using the router which can be injected on the
     * tenant implementation
     * @param request
     * @param returnUrl
     */
    editConnection(request: ITCIConnectionRequest, returnUrl: string): Observable<ITCIConnectionResponse>;

    /**
     * check if the tenant supports the connectorType
     * @param tenantType
     */
    canSupport(connectorType: ITCIConnector): Observable<boolean>;

    /**
     * get connected app count
     * @param request
     */
    getConnectedAppCount(request: ITCIConnectionRequest): Observable<number>;

    /**
     * Show connected apps if allowed by the tenant
     * @param request
     */
    showConnectedApps(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Get connected app id's for the given connection id
     * @param request
     */
    getConnectedApps(request: ITCIConnectionRequest): Observable<string[]>;

    /**
     * check if the connection can be deleted
     * @param request
     */
    canDelete(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * delete Connection
     * @param connection
     */
    deleteConnection(request: ITCIConnectionRequest): Observable<ITCIConnectionResponse>;

    /**
     * Show tooltip text only when canDelete() == false
     * @param request
     */
    getDeleteTooltip(request: ITCIConnectionRequest): Observable<string>;

    /**
     * Indicate if this connection has connection status which may put a badge on the connection in
     * connection list page
     */
    hasConnectionStatus(request: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Get the connection status msg for display
     * @param request
     */
    getConnectionStatus(request: ITCIConnectionRequest): Observable<ITCIConnectionResponse>;

    /**
     * Disconnected Components that take actions related to a connection
     * which impacts the Connection list UI state need to enclose the
     * actions within the callback so the connection provider can let
     * other dependencies get notified on the completion of the action
     * for performing refresh or any other action
     * @param input
     * @param callback
     */
    pushConnectionChanges(
        input: ITCIConnectionRequest,
        callback: (cinput: ITCIConnectionRequest) => Observable<ITCIConnectionResponse>);


    /**
     * Return state if the provider is busy waiting for connection actions to finish
     * @param input
     */
    isBusy(input: ITCIConnectionRequest): Observable<boolean>;

    /**
     * The UI will show/hide visibility column based on this result true/false
     * The tenants e.g. Flogo TCI and Flogo Enterprise will have different results
     * @param input
     * @deprecated
     */
    canShowConnectionVisibility(input: ITCIConnectionRequest): Observable<boolean>;

    /**
     * Return the visibility status for a given {@ITCIConnection}
     * The changing of the visibility from hamburger menu code needs to be
     * wrapped inside pushConnectionChanges() which will trigger a refresh
     * of the Connection List via the NOTIFICATION_REFRESH and conn list
     * will call this provider function to get individual connection visibility
     * status
     * @param input
     * @deprecated
     */
    getConnectionVisibility(input: ITCIConnectionRequest): Observable<ITCIConnectionResponse>;
}

/**
 * Enumeration of TCI menu types
 */
export const TCI_CONNECTIONS_MENU_TYPE: ITCIOptionType<string> = {
    MAIN_MENU: "main",
    HAMBURGER_MENU: "hamburger"
};
export type TCI_CONNECTIONS_MENU_TYPE = keyof typeof TCI_CONNECTIONS_MENU_TYPE;

/**
 * class TCITenantConnectionsMenu
 */
export class TCITenantConnectionsMenu
    extends AbstractTCITenantComponentOptions<TCI_CONNECTIONS_MENU_TYPE>
    implements ITCITenantComponentOptions<TCI_CONNECTIONS_MENU_TYPE>,
    ITCITenantModalOptions {
    public static TCI_CONN_MENU_RESET_METADATA = new TCITenantConnectionsMenu(
        "Reset MetaData",
        TCITenantSelectorType.TCI_CONN_MENU_RESET_METADATA,
        1,
        "connresetmetadata",
        [
            TCITenantAppType.scribe,
            TCITenantAppType.ucs,
        ],
        TCI_CONNECTIONS_MENU_TYPE.HAMBURGER_MENU,
        TCITenantModalType.TCI_MODAL_MEDIUM);

    public static TCI_CONN_MENU_CHANGE_VISIBILITY = new TCITenantConnectionsMenu(
        `Change visibility to "Org"`,
        TCITenantSelectorType.TCI_CONN_MENU_CHANGE_VISIBILITY,
        1,
        "connchgvisibility",
        [
            TCITenantAppType.flogo,
        ],
        TCI_CONNECTIONS_MENU_TYPE.HAMBURGER_MENU,
        TCITenantModalType.TCI_MODAL_SMALL);

    private static allItems = [
        TCITenantConnectionsMenu.TCI_CONN_MENU_RESET_METADATA,
        TCITenantConnectionsMenu.TCI_CONN_MENU_CHANGE_VISIBILITY
    ];

    /**
     * Gets All menu items
     * @param filter
     * @return @ITCITenantMenu[]
     */
    public static getAllMenuItems(
        filter?: (v: TCITenantConnectionsMenu) => boolean): ITCITenantComponentOptions<TCI_CONNECTIONS_MENU_TYPE>[] {
        if (filter) {
            return TCITenantConnectionsMenu.allItems.filter(filter);
        }
        return TCITenantConnectionsMenu.allItems;
    }

    /**
     * constructor
     * @param menuOption
     * @param componentSelectorType
     * @param position
     * @param routeId
     * @param tenantAppTypes
     * @param menuType
     * @param modalType
     */
    constructor(
        menuOption: string,
        componentSelectorType: TCITenantSelectorType,
        position: number,
        routeId: string,
        tenantAppTypes: TCITenantAppType[],
        menuType: TCI_CONNECTIONS_MENU_TYPE = TCI_CONNECTIONS_MENU_TYPE.HAMBURGER_MENU,
        private modalType: ITCITenantModalType = TCITenantModalType.TCI_MODAL_MEDIUM
    ) {
        super(menuOption, componentSelectorType, position, menuType, routeId, tenantAppTypes, null);
    }

    getModalType(): ITCITenantModalType {
        return this.modalType;
    }
}

export interface IConnVisibilityCategory {
    label: string;
    iconUrl: string;
    id: string;
}

export class TCIConnectionVisibility implements IConnVisibilityCategory {
    public static OWNER = new TCIConnectionVisibility("OWNER", "Only me", "ng-ic-visibility-owner.svg", 0);
    public static ORG = new TCIConnectionVisibility("ORG", "Org", "ng-ic-visibility-org.svg", 1);
    public static UNKNOWN = new TCIConnectionVisibility("UNKNOWN", "Unknown", "", 2);

    constructor(private _id: string, private _label: string, private _iconUrl: string, private _order: number) {
    }

    get id() {
        return this._id;
    }

    get label() {
        return this._label;
    }

    get iconUrl() {
        return this._iconUrl.length > 0 ? this._iconUrl : undefined;
    }

    get order() {
        return this._order;
    }

    public static fromId(id: string): IConnVisibilityCategory {
        switch (id) {
            case "OWNER":
                return TCIConnectionVisibility.OWNER;
            case "ORG":
                return TCIConnectionVisibility.ORG;
            case "UNKNOWN":
                return TCIConnectionVisibility.UNKNOWN;
        }
    }

    public static getAllAppTypes(): IConnVisibilityCategory[] {
        return <IConnVisibilityCategory[]>[
            TCIConnectionVisibility.OWNER,
            TCIConnectionVisibility.ORG,
            TCIConnectionVisibility.UNKNOWN
        ];
    }
}
